import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import '@fontsource-variable/manrope';
import './index.css';
import './theme.css';
import './publicTextSelection.css';
import './tooltipTheme.css';
import './brandTheme.css';
import { RootRoute } from './components/RootRoute';
import { authSessionPresenceService } from './services/authSessionPresence';
import { canHydratePublicLandingPrerender } from './utils/prerenderHydration';
import { ThemeProvider } from './contexts/ThemeProvider';
import { CookieService } from './services/cookies';
import { cookieConsentService } from './services/cookieConsent';
import { startBrowserPageViewTracking } from './services/analytics/browserPageViewTracking';
import { applyAppTheme, resolveAppTheme } from './utils/appTheme';
import { readInitialPublicLanguageRedirect } from './utils/publicRoutes';
import { readLanguagePreference } from './utils/languagePreference';

type WindowWithIdleCallback = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
};

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

function syncAnalyticsConsent() {
  void import('./services/analytics').then(({ analyticsService }) => {
    analyticsService.init();
  });
}

function scheduleAnalyticsInit() {
  const initAnalytics = () => {
    syncAnalyticsConsent();
  };

  const browserWindow = window as WindowWithIdleCallback;
  if (typeof browserWindow.requestIdleCallback === 'function') {
    browserWindow.requestIdleCallback(initAnalytics, { timeout: 3_000 });
    return;
  }

  window.setTimeout(initAnalytics, 1_000);
}

function scheduleAnalyticsInitAfterConsent() {
  return cookieConsentService.onChange(syncAnalyticsConsent);
}

function canHydratePrerenderedMarkup(rootElement: HTMLElement) {
  return canHydratePublicLandingPrerender(
    window.location.pathname,
    rootElement.hasChildNodes(),
    undefined,
    rootElement.dataset.prerenderPath,
  );
}

function renderApp(rootElement: HTMLElement, shouldHydrate: boolean) {
  const app = (
    <StrictMode>
      <ThemeProvider>
        <RootRoute />
      </ThemeProvider>
    </StrictMode>
  );

  if (shouldHydrate) {
    hydrateRoot(rootElement, app);
    return;
  }

  rootElement.replaceChildren();
  createRoot(rootElement).render(app);
}

const publicLanguageRedirect = readInitialPublicLanguageRedirect(readLanguagePreference());

if (publicLanguageRedirect) {
  window.location.replace(publicLanguageRedirect);
} else {
  applyAppTheme(resolveAppTheme(CookieService.getAppTheme()));
  startBrowserPageViewTracking(undefined, true);
  scheduleAnalyticsInit();
  scheduleAnalyticsInitAfterConsent();

  const hasHiddenPrerenderedPublicShell =
    authSessionPresenceService.prepareAuthenticatedBoot(rootElement);

  renderApp(rootElement, !hasHiddenPrerenderedPublicShell && canHydratePrerenderedMarkup(rootElement));

  if (hasHiddenPrerenderedPublicShell) {
    window.requestAnimationFrame(() => authSessionPresenceService.finishAuthenticatedBoot());
  }
}
